import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoMdClose, IoMdSearch } from "react-icons/io";
import { toast } from "react-toastify";

import {
  BackContainer,
  ButtonsContainer,
  CloseButton,
  Container,
  ContentContainer,
  EmptyMessage,
  FullLoader,
  FullLoaderContainer,
  HeaderContainer,
  MainButton,
  SearchIconWrapper,
  SearchInput,
  SearchWrapper,
  SelectionSummary,
  SignatureCheckbox,
  SignatureContainer,
  SignatureImage,
  SignatureName,
  SignatureSubtitle,
  SignatureTextContainer,
  SignaturesContainer,
} from "./styles";
import { producaov2 } from "../../../../../../../services/apis";
import { getNodeToken } from "../../../../../../../services/auth_node";
import { useDebounce } from "../../../../../../../hooks/useDebounce";

const SEARCH_DEBOUNCE_TIME = 400;
const SIGNATURES_LIMIT = 100;

function SignatureItem({
  id,
  title,
  subtitle,
  image,
  config,
  isSelected,
  onChangeSelection
}) {

  return (
    <SignatureContainer isSelected={isSelected}>
      <SignatureImage src={image} config={config} />
      <SignatureTextContainer>
        <SignatureName>{title}</SignatureName>
        <SignatureSubtitle>{subtitle}</SignatureSubtitle>
      </SignatureTextContainer>
      <SignatureCheckbox
        type="checkbox"
        checked={!!isSelected}
        onChange={onChangeSelection}
      />
    </SignatureContainer>
  );
}

function SignaturesList({
  signatures,
  isLoading,
  hasSearchTerm,
  selectedSignatures,
  onChangeSelectedSignature
}) {
  if (isLoading) {
    return (
      <FullLoaderContainer>
        <FullLoader></FullLoader>
      </FullLoaderContainer>
    )
  }

  if (!signatures || signatures.length === 0) {
    return (
      <EmptyMessage>
        {hasSearchTerm
          ? "Nenhuma assinatura encontrada para essa busca."
          : "Nenhuma assinatura disponível."}
      </EmptyMessage>
    )
  }

  return (
    <SignaturesContainer>
      {signatures?.map(signature => {
        return (
          <SignatureItem
            key={signature?.id}
            {...signature}
            onChangeSelection={() => onChangeSelectedSignature(signature)}
            isSelected={!!selectedSignatures?.find(selectedSignature => selectedSignature?.id === signature?.id)}
          />
        )
      })}
    </SignaturesContainer>
  );
}

/**
 * Modal dedicado de seleção de assinaturas pro wizard de turmas.
 * Diferente do SignaturesListModal (fluxo avulso), este busca por nome
 * e carrega todas as assinaturas Voitto (não só as 10 primeiras).
 */
export default function SignaturesModal({
  initialSelectedSignatures = [],
  onSubmit,
  onClose
}) {
  const [selectedSignatures, setSelectedSignatures] = useState(initialSelectedSignatures);
  const [searchTerm, setSearchTerm] = useState('');
  const [signatures, setSignatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_TIME);
  const reqSeq = useRef(0);

  const loadSignatures = useCallback(async (search) => {
    const seq = ++reqSeq.current;
    setIsLoading(true);

    try {
      const response = await producaov2.get('/admin/certificate/signatures', {
        params: {
          search: search || undefined,
          limit: SIGNATURES_LIMIT,
        },
        headers: {
          access_token: `voitto-token ${getNodeToken()}`
        }
      });

      if (seq !== reqSeq.current) return;

      setSignatures(Array.isArray(response.data?.signatures) ? response.data.signatures : []);
    } catch(error) {
      if (seq !== reqSeq.current) return;

      toast.error('Não foi possível buscar as assinaturas.');
    }
    finally {
      if (seq === reqSeq.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSignatures(debouncedSearchTerm);
  }, [debouncedSearchTerm, loadSignatures]);

  const onChangeSelectedSignature = (signature) => {
    setSelectedSignatures(prev => {
      const selectedSignature = prev?.find(prevSignature => prevSignature?.id === signature?.id);

      if(selectedSignature) {
        return prev?.filter(prevSignature => prevSignature?.id !== signature?.id);
      }

      return [...prev, signature];
    })
  }

  const onCloseModal = () => {
    onClose();
  }

  const onSubmitSignatures = () => {
    onSubmit(selectedSignatures);
    onCloseModal();
  }

  return createPortal(
    <Container>
      <BackContainer onClick={onCloseModal}>
        <ContentContainer onClick={(e) => e?.stopPropagation()}>
          <HeaderContainer>
            <SearchWrapper>
              <SearchIconWrapper>
                <IoMdSearch size="1.2em" />
              </SearchIconWrapper>
              <SearchInput
                type="text"
                placeholder="Buscar assinatura pelo nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </SearchWrapper>
            <CloseButton onClick={onCloseModal}>
              <IoMdClose size="1.5em" />
            </CloseButton>
          </HeaderContainer>
          <SignaturesList
            signatures={signatures}
            isLoading={isLoading}
            hasSearchTerm={!!debouncedSearchTerm}
            selectedSignatures={selectedSignatures}
            onChangeSelectedSignature={onChangeSelectedSignature}
          />
          <ButtonsContainer>
            <SelectionSummary>
              {selectedSignatures?.length || 0} selecionada{selectedSignatures?.length === 1 ? '' : 's'}
            </SelectionSummary>
            <MainButton onClick={onSubmitSignatures} disabled={selectedSignatures?.length === 0}>Selecionar assinaturas</MainButton>
          </ButtonsContainer>
        </ContentContainer>
      </BackContainer>
    </Container>,
    document.body
  );
}
